import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert, Tooltip } from '@mui/material';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { downloadHansardXml } from '../services/api';

/**
 * DownloadXmlButton — downloads the source XML for a Hansard record.
 * Serves the original raw APH XML when held; otherwise a clearly-labelled
 * ParlSearch structured XML export. Mirrors the DownloadPdfButton pattern.
 *
 * @param {string} contentId - the Hansard fragment id
 * @param {string} size      - MUI button size (default 'small')
 */
export default function DownloadXmlButton({ contentId, size = 'small' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleDownload = async () => {
    if (loading || !contentId) return;
    setLoading(true);
    setError(null);
    try {
      await downloadHansardXml(contentId);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'XML download failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tooltipText = 'Download the source XML for this Hansard record';

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <span>
          <Button
            variant="outlined"
            size={size}
            startIcon={loading
              ? <CircularProgress size={16} aria-label="Preparing XML" />
              : <DataObjectIcon />}
            onClick={handleDownload}
            disabled={loading || !contentId}
            aria-busy={loading}
            aria-label={loading ? 'Preparing XML, please wait' : tooltipText}
            sx={{
              textTransform: 'none',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            {loading ? 'Preparing…' : 'Download XML'}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" role="alert" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" role="status" onClose={() => setSuccess(false)}>
          XML download started. Check your downloads folder.
        </Alert>
      </Snackbar>
    </>
  );
}
